/**
 * Import Page
 *
 * Import products, categories, and other data from WooCommerce and other platforms.
 */

import { useState, useCallback } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { api } from '../../lib/api';

interface Store {
  id: string;
  name: string;
  slug: string;
}

interface OutletContext {
  store: Store;
}

interface ImportJob {
  id: string;
  source: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  total_items: number;
  processed_items: number;
  failed_items: number;
  error_log: string | null;
  created_at: string;
  completed_at: string | null;
}

type ImportSource = 'woocommerce' | 'csv' | 'shopify';

export default function ImportPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const context = useOutletContext<OutletContext | null>();
  const store = context?.store;

  const [activeTab, setActiveTab] = useState<ImportSource>('woocommerce');
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentJob, setCurrentJob] = useState<ImportJob | null>(null);

  // WooCommerce form
  const [wooUrl, setWooUrl] = useState('');
  const [wooKey, setWooKey] = useState('');
  const [wooSecret, setWooSecret] = useState('');
  const [wooOptions, setWooOptions] = useState({
    products: true,
    categories: true,
    images: true,
    variations: true,
  });

  // CSV form
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvType, setCsvType] = useState<'products' | 'categories'>('products');

  const handleWooCommerceImport = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = store?.id || storeId;
    if (!id) return;

    try {
      setImporting(true);
      setError(null);
      setSuccess(null);

      const response = await api.post(`/api/ecommerce/stores/${id}/import/woocommerce`, {
        site_url: wooUrl,
        consumer_key: wooKey,
        consumer_secret: wooSecret,
        options: wooOptions,
      });

      const data = await response.json() as { job: ImportJob; message: string };
      setCurrentJob(data.job);
      setSuccess(data.message || 'Import started successfully');

      // Poll for updates
      pollJobStatus(data.job.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const handleCSVImport = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = store?.id || storeId;
    if (!id || !csvFile) return;

    try {
      setImporting(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('type', csvType);

      const response = await fetch(`/api/ecommerce/stores/${id}/import/csv`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const data = await response.json() as { job: ImportJob; message: string };
      setCurrentJob(data.job);
      setSuccess(data.message || 'Import started successfully');

      // Poll for updates
      pollJobStatus(data.job.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start import');
    } finally {
      setImporting(false);
    }
  };

  const pollJobStatus = useCallback(async (jobId: string) => {
    const id = store?.id || storeId;
    if (!id) return;

    const checkStatus = async () => {
      try {
        const response = await api.get(`/api/ecommerce/stores/${id}/import/jobs/${jobId}`);
        const data = await response.json() as { job: ImportJob };
        setCurrentJob(data.job);

        if (data.job.status === 'processing' || data.job.status === 'pending') {
          setTimeout(checkStatus, 2000);
        } else if (data.job.status === 'completed') {
          setSuccess(`Import completed! ${data.job.processed_items} items imported.`);
        } else if (data.job.status === 'failed') {
          setError(`Import failed: ${data.job.error_log || 'Unknown error'}`);
        }
      } catch {
        // Silently fail polling
      }
    };

    setTimeout(checkStatus, 2000);
  }, [store?.id, storeId]);

  const downloadTemplate = (type: 'products' | 'categories') => {
    let content = '';
    let filename = '';

    if (type === 'products') {
      content = 'name,description,price,sale_price,category,image_url,stock_quantity,status\n';
      content += '"Example Product","Product description",1999,1499,"Category Name","https://example.com/image.jpg",100,active\n';
      filename = 'products-template.csv';
    } else {
      content = 'name,description,parent_category,image_url\n';
      content += '"Example Category","Category description","","https://example.com/image.jpg"\n';
      filename = 'categories-template.csv';
    }

    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Import Data</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-1">
          Import products, categories, and more from external platforms
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {success}
          <button onClick={() => setSuccess(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Current Job Status */}
      {currentJob && (currentJob.status === 'pending' || currentJob.status === 'processing') && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <div>
              <div className="font-medium text-blue-700 dark:text-blue-300">
                Import in progress...
              </div>
              <div className="text-sm text-blue-600 dark:text-blue-400">
                {currentJob.processed_items} / {currentJob.total_items || '?'} items processed
              </div>
            </div>
          </div>
          {currentJob.total_items > 0 && (
            <div className="mt-3 h-2 bg-blue-200 dark:bg-blue-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-600 dark:bg-blue-400 transition-all duration-300"
                style={{ width: `${(currentJob.processed_items / currentJob.total_items) * 100}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex gap-6">
          {[
            { id: 'woocommerce' as const, label: 'WooCommerce', icon: '🛒' },
            { id: 'csv' as const, label: 'CSV File', icon: '📄' },
            { id: 'shopify' as const, label: 'Shopify', icon: '🛍️', disabled: true },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => !tab.disabled && setActiveTab(tab.id)}
              disabled={tab.disabled}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-teal-600 text-teal-600 dark:text-teal-400 dark:border-teal-400'
                  : tab.disabled
                  ? 'border-transparent text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
              {tab.disabled && <span className="ml-1 text-xs">(Coming soon)</span>}
            </button>
          ))}
        </nav>
      </div>

      {/* WooCommerce Import */}
      {activeTab === 'woocommerce' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Import from WooCommerce
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Connect to your WooCommerce store using REST API credentials. You can find these in
              WooCommerce → Settings → Advanced → REST API.
            </p>
          </div>

          <form onSubmit={handleWooCommerceImport} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Store URL
              </label>
              <input
                type="url"
                value={wooUrl}
                onChange={(e) => setWooUrl(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                placeholder="https://yourstore.com"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Consumer Key
                </label>
                <input
                  type="text"
                  value={wooKey}
                  onChange={(e) => setWooKey(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-mono text-sm"
                  placeholder="ck_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Consumer Secret
                </label>
                <input
                  type="password"
                  value={wooSecret}
                  onChange={(e) => setWooSecret(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-mono text-sm"
                  placeholder="cs_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                What to import
              </label>
              <div className="space-y-2">
                {[
                  { key: 'products', label: 'Products' },
                  { key: 'categories', label: 'Categories' },
                  { key: 'images', label: 'Product Images' },
                  { key: 'variations', label: 'Product Variations' },
                ].map((option) => (
                  <label key={option.key} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={wooOptions[option.key as keyof typeof wooOptions]}
                      onChange={(e) => setWooOptions({ ...wooOptions, [option.key]: e.target.checked })}
                      className="rounded border-gray-300 dark:border-gray-600 text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              disabled={importing}
              className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {importing ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Importing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Start Import
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* CSV Import */}
      {activeTab === 'csv' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Import from CSV
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Upload a CSV file with your products or categories. Download the template to see the required format.
            </p>
          </div>

          <div className="flex gap-2 mb-6">
            <button
              onClick={() => downloadTemplate('products')}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Download Products Template
            </button>
            <button
              onClick={() => downloadTemplate('categories')}
              className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Download Categories Template
            </button>
          </div>

          <form onSubmit={handleCSVImport} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Import Type
              </label>
              <select
                value={csvType}
                onChange={(e) => setCsvType(e.target.value as typeof csvType)}
                className="w-full px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
              >
                <option value="products">Products</option>
                <option value="categories">Categories</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                CSV File
              </label>
              <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer"
                >
                  <svg className="w-10 h-10 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  {csvFile ? (
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">{csvFile.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {(csvFile.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        Click to upload or drag and drop
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        CSV file only
                      </div>
                    </div>
                  )}
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={importing || !csvFile}
              className="w-full px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {importing ? (
                <>
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Importing...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  Upload & Import
                </>
              )}
            </button>
          </form>
        </div>
      )}

      {/* Shopify (Coming Soon) */}
      {activeTab === 'shopify' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🛍️</span>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Shopify Import Coming Soon
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            We're working on adding support for importing from Shopify stores.
          </p>
        </div>
      )}

      {/* Import Tips */}
      <div className="mt-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
        <h3 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
          Import Tips
        </h3>
        <ul className="text-sm text-yellow-700 dark:text-yellow-300 space-y-1">
          <li>• Make sure your source store is accessible and credentials are correct</li>
          <li>• Large imports may take several minutes to complete</li>
          <li>• Product images will be downloaded and stored in your media library</li>
          <li>• Existing products with the same SKU will be updated, not duplicated</li>
          <li>• You can close this page - the import will continue in the background</li>
        </ul>
      </div>
    </div>
  );
}
